import { useFetchTenantBrandingQuery } from "queries/tenant.queries";

// Fallbacks used before tenant branding loads (or if it's missing colors).
const DEFAULT_BACKGROUND = "#1a1a2e";
const DEFAULT_ACCENT = "#e94560";

export interface RsvpColors {
  background: string;
  accent: string;
  accentTint: string;
  onAccent: string; // text/icon color that contrasts with `accent`
  textPrimary: string;
  textSecondary: string;
  textMuted: string;
  textFaint: string;
  cardBg: string;
  cardBorder: string;
  inputBg: string;
  inputBorder: string;
}

/** Parse a #rgb / #rrggbb hex into RGB components, or null if malformed. */
const hexToRgb = (hex: string): { r: number; g: number; b: number } | null => {
  const parsed = hex.replace("#", "").trim();
  const full =
    parsed.length === 3
      ? parsed
          .split("")
          .map((c) => c + c)
          .join("")
      : parsed;
  const r = parseInt(full.slice(0, 2), 16);
  const g = parseInt(full.slice(2, 4), 16);
  const b = parseInt(full.slice(4, 6), 16);
  if ([r, g, b].some((n) => Number.isNaN(n))) return null;
  return { r, g, b };
};

/** Convert a hex color to an rgba() string; falls back to the input on error. */
export const hexToRgba = (hex: string, alpha: number): string => {
  const rgb = hexToRgb(hex);
  if (!rgb) return hex;
  return `rgba(${rgb.r},${rgb.g},${rgb.b},${alpha})`;
};

/** True when a color is light enough to need dark text on top of it. */
export const isLightColor = (hex: string): boolean => {
  const rgb = hexToRgb(hex);
  if (!rgb) return false;
  // Perceived brightness (YIQ). 0 = black, 255 = white.
  const brightness = (rgb.r * 299 + rgb.g * 587 + rgb.b * 114) / 1000;
  return brightness > 150;
};

// Text + translucent-overlay scheme for a DARK background (light content).
const ON_DARK = {
  textPrimary: "#f8fafc", // slate-50
  textSecondary: "#e2e8f0", // slate-200
  textMuted: "#cbd5e1", // slate-300
  textFaint: "#94a3b8", // slate-400
  cardBg: "rgba(255,255,255,0.06)",
  cardBorder: "rgba(255,255,255,0.14)",
  inputBg: "rgba(255,255,255,0.10)",
  inputBorder: "rgba(255,255,255,0.22)",
};

// Text + translucent-overlay scheme for a LIGHT background (dark content).
const ON_LIGHT = {
  textPrimary: "#0f172a", // slate-900
  textSecondary: "#1e293b", // slate-800
  textMuted: "#475569", // slate-600
  textFaint: "#64748b", // slate-500
  cardBg: "rgba(0,0,0,0.04)",
  cardBorder: "rgba(0,0,0,0.12)",
  inputBg: "rgba(0,0,0,0.05)",
  inputBorder: "rgba(0,0,0,0.18)",
};

/**
 * Tenant-themed, contrast-aware RSVP palette. `background` comes from the
 * tenant primaryColor and `accent` from secondaryColor; text and overlay
 * colors flip based on the background's luminance so the pages stay legible
 * on light, dark, or saturated brand colors.
 */
export const useRsvpColors = (): RsvpColors => {
  const { data: branding } = useFetchTenantBrandingQuery();
  const background = branding?.primaryColor || DEFAULT_BACKGROUND;
  const accent = branding?.secondaryColor || DEFAULT_ACCENT;
  const scheme = isLightColor(background) ? ON_LIGHT : ON_DARK;
  return {
    background,
    accent,
    accentTint: hexToRgba(accent, 0.15),
    onAccent: isLightColor(accent) ? "#0f172a" : "#ffffff",
    ...scheme,
  };
};

export type RsvpTone = "success" | "warning" | "info" | "danger" | "neutral";

export const rsvpTone: Record<RsvpTone, { color: string; iconBg: string }> = {
  success: { color: "#10b981", iconBg: "rgba(16,185,129,0.2)" },
  warning: { color: "#f59e0b", iconBg: "rgba(245,158,11,0.2)" },
  info: { color: "#3b82f6", iconBg: "rgba(59,130,246,0.2)" },
  danger: { color: "#ef4444", iconBg: "rgba(239,68,68,0.12)" },
  neutral: { color: "#9ca3af", iconBg: "rgba(107,114,128,0.2)" },
};
